import { useEffect, useState } from "react";
import styled from "styled-components";

import {
  AddOutlined,
  DeleteOutline as DeleteOutlined,
  PeopleOutline,
  SaveOutlined,
  ArrowBack,
} from "@material-ui/icons";

import { useHistory } from "react-router-dom";

import Loader from "../../components/Loader";
import AddUserModal from "../../components/admin/AddUserModal";
import ActionButton from "../../components/buttons/Action";
import fetchClient from "../../api/fetchClient";
import message from "../../helper/message";

const permissionGroups = [
  {
    groupName: "Geral",
      permissions: [
        { label: "Acesso Total", value: "acesso-total" },
        { label: "Administrador", value: "admin" },
        { label: "Seidor", value: "seidor" },
        { label: "Econodata", value: "econodata" },
      ],
    },
    {
      groupName: "Concur",
      permissions: [
        { label: "Card Principal", value: "concur" },
        { label: "Empresas", value: "concur-empresas" },
        { label: "Dashboard", value: "concur-dashboard" },
        { label: "Configurações", value: "concur-config" },
        { label: "Logs", value: "concur-logs" },
      ],
    },
    {
      groupName: "Viceri",
      permissions: [
        { label: "Card Principal", value: "viceri" },
        { label: "Dashboard", value: "viceri-dashboard" },
        { label: "Aplicações", value: "viceri-aplicacoes" },
        { label: "Logs", value: "viceri-logs" },
      ],
    },
    {
      groupName: "Comerzzia",
      permissions: [
        { label: "Card Principal", value: "comerzzia" },
        { label: "Dashboard", value: "comerzzia-dashboard" },
        { label: "Aplicações", value: "comerzzia-aplicacoes" },
        { label: "Grupos", value: "comerzzia-grupos" },
        { label: "Notificações", value: "comerzzia-notificacoes" },
        { label: "Utilização", value: "comerzzia-usage" },
        { label: "Worker Dashboard", value: "comerzzia-worker" },
      ],
    },
    {
      groupName: "IFS",
      permissions: [
        { label: "Card Principal", value: "ifs" },
        { label: "Aplicações", value: "ifs-aplicacoes" },
        { label: "Grupos", value: "ifs-grupos" },
        { label: "Fila de Integração", value: "ifs-fila" },
        { label: "Logs", value: "ifs-logs" },
        { label: "Sincronizar Estoque", value: "ifs-sync-estoque" },
      ],
    },
    {
      groupName: "Ariba",
      permissions: [
        { label: "Card Principal", value: "ariba" },
        { label: "Empresas", value: "ariba-empresas" },
        { label: "Grupos", value: "ariba-grupos" },
        { label: "Logs", value: "ariba-logs" },
        { label: "Reprocessamento", value: "ariba-reprocessamento" },
        { label: "Dashboard Métricas", value: "ariba-dashboard-metricas" },
      ],
    },
    {
      groupName: "EssentialOne",
      permissions: [
        { label: "Card Principal", value: "e1" },
        { label: "Dashboard", value: "e1-dashboard" },
        { label: "Dashboard Consolidado", value: "e1-dashboard-consolidado" },
      ],
    },
    {
      groupName: "Senior",
      permissions: [
        { label: "Card Principal", value: "senior" },
        { label: "WMS", value: "senior-wms" },
        { label: "Grupos", value: "senior-grupos" },
        { label: "Logs", value: "senior-logs" },
        { label: "Dashboard Métricas", value: "senior-wms-metrics" },
      ],
    },
  ];

const normalizePermissions = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return Array.from(new Set(value)).sort();
  return Array.from(new Set(String(value).split("|").filter(Boolean))).sort();
};

export default function AdminUsers() {
  const history = useHistory();

  const [users, setUsers] = useState(null);
  const [permissionDrafts, setPermissionDrafts] = useState({});
  const [addingUser, setAddingUser] = useState(false);

  const [loadingDelete, setLoadingDelete] = useState(-1);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  async function getUsers() {
    setIsLoadingUsers(true);
    try {
      const { data } = await fetchClient.get("/users");
      setUsers(data);
    } catch (error) {
      console.error(error);
      message.error("Erro ao carregar os usuários");
    } finally {
      setIsLoadingUsers(false);
    }
  }

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (!users) return;
    const drafts = {};
    users.forEach((user) => {
      drafts[user.username] = normalizePermissions(user.permissions);
    });
    setPermissionDrafts(drafts);
  }, [users]);

  const handlePermissionToggle = (username, permission) => {
    setPermissionDrafts((prev) => {
      const current = prev[username] ? [...prev[username]] : [];
      const hasPermission = current.includes(permission);
      const hasFullAccess = current.includes("acesso-total");
      const isFullAccessToggle = permission === "acesso-total";

      // Quando Acesso Total é marcado, zera demais permissões e mantém só ele.
      if (isFullAccessToggle) {
        const next = hasPermission ? [] : ["acesso-total"];
        return {
          ...prev,
          [username]: next,
        };
      }

      // Não permitir alterar outros cards enquanto Acesso Total estiver ativo.
      if (hasFullAccess) {
        return prev;
      }

      // Check if it's a main card permission being unchecked
      const mainCardPermissions = ['concur', 'viceri', 'comerzzia', 'ifs', 'ariba', 'senior'];
      if (hasPermission && mainCardPermissions.includes(permission)) {
        const confirmed = window.confirm(
          'Ao desmarcar o "Card Principal", o usuário não poderá acessar nenhum dos subcards deste módulo.\n\nDeseja continuar?'
        );
        if (!confirmed) {
          return prev; // Don't change anything if user cancels
        }
      }

      const next = hasPermission
        ? current.filter((perm) => perm !== permission)
        : [...current, permission];
      return {
        ...prev,
        [username]: next,
      };
    });
  };

  async function handleSave() {
    if (!users) return;
    try {
      setLoadingSave(true);
      const toSave = [];

      users.forEach((user) => {
        const currentPermissions = normalizePermissions(
          permissionDrafts[user.username]
        );
        const originalPermissions = normalizePermissions(user.permissions);

        if (currentPermissions.join("|") !== originalPermissions.join("|")) {
          toSave.push({
            username: user.username,
            permissions: currentPermissions,
          });
        }
      });

      if (toSave.length === 0) {
        message.sucess("Nenhuma alteração para salvar");
        return;
      }

      for (const { username, permissions } of toSave) {
        await fetchClient.patch(`/users/${username}`, {
          permissions,
        });
      }

      await getUsers();
      message.sucess("Usuários salvos com sucesso!");
    } catch (err) {
      console.error(err);
      message.error("Erro ao salvar os usuários");
    } finally {
      setLoadingSave(false);
    }
  }

  async function handleRegister(email) {
    try {
      setLoadingAdd(true);
      setAddingUser(false);
      await fetchClient.post("/users", {
        email,
      });
      message.sucess("Usuário registrado com sucesso!");
    } catch (error) {
      message.error(error.response?.data?.message ?? "Erro ao registrar usuário");
    } finally {
      setLoadingAdd(false);
      await getUsers();
    }
  }

  async function handleResetPassword(username) {
    try {
      const { data } = await fetchClient.put(`/users/${username}`);
      message.sucess(data.message);
      await getUsers();
    } catch (error) {
      message.error(error.response?.data?.message ?? "Erro ao resetar a senha");
    }
  }

  async function handleDelete(username, index) {
    const confirmDelete = window.confirm(
      `Tem certeza que deseja deletar o usuário "${username}"? Esta ação não pode ser desfeita.`
    );

    if (!confirmDelete) return;

    try {
      setLoadingDelete(index);
      const { data } = await fetchClient.delete(`/users/${username}`);
      message.sucess(data.message);
      await getUsers();
    } catch (error) {
      message.error(error.response?.data?.message ?? "Erro ao excluir o usuário");
    } finally {
      setLoadingDelete(-1);
    }
  }

  const hasUsers = users && users.length > 0;

  // Filtrar usuários
  const filteredUsers = users
    ? users.filter((user) => {
        const term = searchTerm.toLowerCase();
        const nameMatch = user.nickname?.toLowerCase().includes(term);
        const emailMatch = user.email?.toLowerCase().includes(term);
        const usernameMatch = user.username?.toLowerCase().includes(term);
        return nameMatch || emailMatch || usernameMatch;
      })
    : [];

  return (
    <Page>
      {addingUser && (
        <AddUserModal
          onRegister={handleRegister}
          onClose={() => setAddingUser(false)}
        />
      )}

      <ModernHeader>
        <HeaderLeft>
          <BackButton onClick={() => history.goBack()}>
            <ArrowBack />
          </BackButton>
          <HeaderContent>
            <HeaderTitle>
              <PeopleOutline style={{ marginRight: "12px", fontSize: "32px" }} />
              Gestão de Usuários
            </HeaderTitle>
            <HeaderSubtitle>Organize permissões e acesso ao portal</HeaderSubtitle>
          </HeaderContent>
        </HeaderLeft>
        <HeaderActions>
          <HeaderActionButton
            variant="primary"
            onClick={handleSave}
            disabled={loadingSave}
          >
            <SaveOutlined />
            {loadingSave ? "Salvando..." : "Salvar permissões"}
          </HeaderActionButton>
          <HeaderActionButton
            variant="secondary"
            onClick={() => setAddingUser(true)}
            disabled={loadingAdd}
          >
            <AddOutlined />
            {loadingAdd ? "Enviando..." : "Adicionar usuário"}
          </HeaderActionButton>
        </HeaderActions>
      </ModernHeader>

      <ContentCard>
        {isLoadingUsers ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : !hasUsers ? (
          <EmptyState>
            <PeopleOutline style={{ fontSize: "48px", color: "#9CA3AF" }} />
            <EmptyTitle>Nenhum usuário encontrado</EmptyTitle>
            <EmptyText>
              Cadastre usuários e defina permissões para liberar o acesso ao portal.
            </EmptyText>
          </EmptyState>
        ) : (
          <>
            <SearchSection>
              <SearchContainer>
                <SearchIcon>
                  <PeopleOutline style={{ fontSize: "20px" }} />
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Buscar por nome, email ou username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={() => setSearchTerm("")}>×</ClearButton>
                )}
              </SearchContainer>
              <SearchInfo>
                Exibindo {filteredUsers.length} de {users.length} usuário(s)
              </SearchInfo>
            </SearchSection>

            {filteredUsers.length === 0 ? (
              <EmptyState>
                <PeopleOutline style={{ fontSize: "48px", color: "#9CA3AF" }} />
                <EmptyTitle>Nenhum usuário encontrado</EmptyTitle>
                <EmptyText>
                  Não há usuários que correspondam à sua busca "{searchTerm}".
                </EmptyText>
              </EmptyState>
            ) : (
              <UsersGrid>
                {filteredUsers.map((user, index) => (
              <UserCard key={user.username || `${user.email}-${index}`}>
                <UserCardHeader>
                  <UserInfo>
                    <UserName>{user.nickname}</UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfo>
                  <DeleteIconButton
                    onClick={() => handleDelete(user.username, index)}
                    disabled={loadingDelete === index}
                  >
                    {loadingDelete === index ? (
                      <LoaderMini />
                    ) : (
                      <DeleteOutlined />
                    )}
                  </DeleteIconButton>
                </UserCardHeader>

                <PermissionsContainer>
                  {permissionGroups.map((group) => (
                    <PermissionGroup key={group.groupName}>
                      <GroupTitle>{group.groupName}</GroupTitle>
                      <PermissionGrid>
                        {group.permissions.map((perm) => (
                          <PermissionItem key={perm.value}>
                          <PermissionCheckbox
                            type="checkbox"
                            id={`${user.username}-${perm.value}`}
                            checked={
                              (permissionDrafts[user.username] || []).includes(
                                perm.value
                              )
                            }
                            disabled={
                              perm.value !== "acesso-total" &&
                              (permissionDrafts[user.username] || []).includes("acesso-total")
                            }
                            onChange={() =>
                              handlePermissionToggle(user.username, perm.value)
                            }
                          />
                            <PermissionLabel htmlFor={`${user.username}-${perm.value}`}>
                              {perm.label}
                            </PermissionLabel>
                          </PermissionItem>
                        ))}
                      </PermissionGrid>
                    </PermissionGroup>
                  ))}
                </PermissionsContainer>
              </UserCard>
                ))}
              </UsersGrid>
            )}
          </>
        )}
      </ContentCard>
    </Page>
  );
}

const Page = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px 48px;
  box-sizing: border-box;
`;

const ModernHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const BackButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  border: none;
  background: rgba(102, 126, 234, 0.1);
  color: #4c1d95;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateX(-2px);
    background: rgba(66, 153, 225, 0.15);
  }

  &:active {
    transform: translateX(0);
  }
`;

const HeaderTitle = styled.h1`
  font-size: 32px;
  color: #1f2933;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  font-family: "Mulish", sans-serif;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 16px;
  font-family: "Mulish", sans-serif;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const headerVariantStyles = {
  primary: `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
  `,
  secondary: `
    background: linear-gradient(135deg, #8e44ad 0%, #d35400 100%);
    color: #fff;
  `,
};

const HeaderActionButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  transition: all 0.25s ease;
  ${({ variant }) => headerVariantStyles[variant] || headerVariantStyles.primary}

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
`;

const LoaderWrapper = styled.div`
  width: 100%;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 60px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #4b5563;
  font-family: "Mulish", sans-serif;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 20px;
`;

const EmptyText = styled.p`
  margin: 0;
  max-width: 420px;
  text-align: center;
  line-height: 1.5;
`;

const SearchSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 48px 14px 52px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  font-family: "Mulish", sans-serif;
  transition: all 0.3s ease;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #8e44ad;
    background: white;
    box-shadow: 0 0 0 4px rgba(142, 68, 173, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-family: "Mulish", sans-serif;

  &:hover {
    background: #d1d5db;
    color: #374151;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SearchInfo = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
  padding-left: 4px;
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 24px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #d1d5db;
  }
`;

const UserCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  font-family: "Mulish", sans-serif;
`;

const UserEmail = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const DeleteIconButton = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
  }

  svg {
    font-size: 20px;
  }
`;

const LoaderMini = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid white;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PermissionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PermissionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  font-family: "Mulish", sans-serif;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PermissionCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #8e44ad;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const PermissionLabel = styled.label`
  font-size: 14px;
  color: #4b5563;
  font-family: "Mulish", sans-serif;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;

  &:hover {
    color: #8e44ad;
  }
`;
